import React, { useEffect, useState, useCallback } from 'react';
import { 
  Bell, BellRing, Clock, CheckCircle, AlertTriangle, 
  Calendar, ChevronRight, Trash2, Check, X, ClipboardList
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

const NotificationCard = ({ notification, onMarkRead, onDelete, onAction, theme }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'task_notification':
        return <ClipboardList size={20} className="text-blue-400" />;
      case 'task_reminder_1h':
        return <AlertTriangle size={20} className="text-red-400" />;
      case 'task_reminder_24h':
        return <Clock size={20} className="text-amber-400" />;
      case 'task_alert':
        return <AlertTriangle size={20} className="text-red-400" />;
      case 'schedule':
        return <Calendar size={20} className="text-cyan-400" />;
      case 'reminder':
        return <BellRing size={20} className="text-amber-400" />;
      default:
        return <Bell size={20} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} />;
    }
  };

  const getBgColor = () => {
    if (notification.read) {
      return theme === 'dark' ? 'border-white/5 bg-zinc-900/30 opacity-70' : 'border-gray-100 bg-gray-50 opacity-70';
    }
    switch (notification.type) {
      case 'task_notification':
        return theme === 'dark' ? 'border-blue-500/30 bg-blue-500/10' : 'border-blue-200 bg-blue-50';
      case 'task_reminder_1h':
        return theme === 'dark' ? 'border-red-500/30 bg-red-500/10' : 'border-red-200 bg-red-50';
      case 'task_reminder_24h':
        return theme === 'dark' ? 'border-amber-500/30 bg-amber-500/10' : 'border-amber-200 bg-amber-50';
      case 'task_alert':
        return theme === 'dark' ? 'border-red-500/30 bg-red-500/10' : 'border-red-200 bg-red-50';
      case 'reminder':
        return theme === 'dark' ? 'border-amber-500/30 bg-amber-500/10' : 'border-amber-200 bg-amber-50';
      default:
        return theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white';
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "À l'instant";
    if (mins < 60) return `Il y a ${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    return `Il y a ${days}j`;
  };

  return (
    <div 
      className={cn('p-4 rounded-xl border transition-all', getBgColor())}
      data-testid={`notification-${notification.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
          theme === 'dark' ? 'bg-black/30' : 'bg-gray-100'
        )}>
          {getIcon()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className={cn(
                'font-semibold text-sm',
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                {notification.title || notification.titre}
              </h3>
              {!notification.read && (
                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full ml-2 align-middle"></span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {!notification.read && notification.id && !notification.id.startsWith('alert-') && (
                <button
                  onClick={() => onMarkRead(notification.id)}
                  className={cn(
                    'p-1 rounded-lg transition-colors',
                    theme === 'dark' ? 'hover:bg-white/10 text-zinc-500 hover:text-green-400' : 'hover:bg-gray-200 text-gray-400 hover:text-green-600'
                  )}
                  title="Marquer comme lu"
                >
                  <Check size={16} />
                </button>
              )}
              <button
                onClick={() => onDelete(notification.id)}
                className={cn(
                  'p-1 rounded-lg transition-colors',
                  theme === 'dark' ? 'hover:bg-white/10 text-zinc-500 hover:text-red-400' : 'hover:bg-gray-200 text-gray-400 hover:text-red-600'
                )}
                title="Supprimer"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          
          <p className={cn('text-sm mt-1', theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
            {notification.message}
          </p>
          
          <div className="flex items-center justify-between mt-3">
            <span className={cn(
              'text-xs flex items-center gap-1',
              theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'
            )}>
              <Clock size={12} />
              {timeAgo(notification.created_at)}
            </span>
            
            {notification.task_id && (
              <Button
                size="sm"
                className={cn(
                  'h-7 px-3 text-xs',
                  theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                )}
                onClick={() => onAction(notification)}
                data-testid={`notification-action-${notification.id}`}
              >
                Voir la tâche
                <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [dashboardAlerts, setDashboardAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { theme } = useTheme();

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, dashboardRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/dashboard')
      ]);
      
      setNotifications(notifRes.data || []);

      const alertNotifications = (dashboardRes.data?.alerts || []).map((alert, idx) => ({
        id: `alert-${idx}`,
        type: 'reminder',
        title: alert.titre,
        message: alert.message,
        created_at: alert.created_at || new Date().toISOString(),
        read: true,
        task_id: null,
        link: alert.link
      }));
      setDashboardAlerts(alertNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      toast.success('Notification marquée comme lue');
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleDelete = async (id) => {
    if (id.startsWith('alert-')) {
      setDashboardAlerts(prev => prev.filter(n => n.id !== id));
      return;
    }
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAction = (notification) => {
    if (notification.task_id) {
      if (!notification.read && notification.id && !notification.id.startsWith('alert-')) {
        handleMarkRead(notification.id);
      }
      navigate('/tasks');
    } else if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    try {
      await Promise.all(unread.map(n => api.put(`/notifications/${n.id}/read`)));
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const allItems = [...notifications, ...dashboardAlerts];
  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className={cn('w-16 h-16 border-4 rounded-full', theme === 'dark' ? 'border-zinc-800' : 'border-gray-200')}></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1 md:p-0 pb-24 md:pb-0" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center',
            theme === 'dark' ? 'bg-red-500/20' : 'bg-red-100'
          )}>
            <Bell size={24} className={theme === 'dark' ? 'text-red-400' : 'text-red-500'} />
          </div>
          <div>
            <h1 className={cn(
              'text-xl font-bold uppercase tracking-wide',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>
              Notifications
            </h1>
            <p className={cn('text-sm', theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
              {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Aucune nouvelle notification'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={handleMarkAllRead}
              className={cn(
                theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:text-white hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
              )}
              data-testid="mark-all-read"
            >
              <Check size={16} className="mr-2" />
              Tout lire
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      {allItems.length > 0 ? (
        <div className="space-y-3">
          {allItems.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
              onAction={handleAction}
              theme={theme}
            />
          ))}
        </div>
      ) : (
        <div className={cn(
          'p-12 text-center rounded-xl border',
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200'
        )}>
          <div className={cn(
            'w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4',
            theme === 'dark' ? 'bg-zinc-800' : 'bg-gray-100'
          )}>
            <CheckCircle size={32} className="text-green-400" />
          </div>
          <h3 className={cn(
            'text-lg font-semibold mb-2',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            Tout est en ordre !
          </h3>
          <p className={cn('text-sm', theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
            Vous n'avez aucune notification pour le moment.
          </p>
        </div>
      )}
    </div>
  );
}
